// hooks/usePlatformManagement.js - Platform management logic and state
// Extracted from PlatformSelector.js and Dashboard.js

import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import TokenStorage from '../services/TokenStorage';
import logger from '../services/logger';
import { API_BASE_URL } from '../config';

/**
 * Custom hook for managing platform connections and switching
 */
export const usePlatformManagement = () => {
  const location = useLocation();
  
  // OAuth success state
  const [showConnectionSuccess, setShowConnectionSuccess] = useState(false);
  const [connectedPlatform, setConnectedPlatform] = useState(null);
  
  // Enhanced platform connection states with username support
  const [platformStates, setPlatformStates] = useState({
    spotify: {
      connected: false,
      active: false,
      username: null,
      status: 'not-connected' // not-connected, connected, active, error
    },
    youtube: {
      connected: false,
      active: false,
      username: null,
      status: 'not-connected'
    }
  });

  /**
   * Check platform connection states from localStorage tokens
   */
  const checkPlatformStates = useCallback(async () => {
    try {
      logger.debug('usePlatformManagement: checkPlatformStates() called');
      logger.progress('Checking platform connection states from localStorage...');

      // Check Flask session tokens (what platform switching actually uses)
      let spotifyConnected = false;
      let youtubeConnected = false;
      let activeData = { spotify: { active: false }, youtube: { active: false } };

      try {
        logger.debug('Making request to /platform-status...');

        // Get Supabase auth token to send with request
        const { supabase } = await import('../lib/supabase');
        const { data: { session } } = await supabase.auth.getSession();

        const headers = {};
        if (session?.access_token) {
          headers['X-Supabase-Token'] = session.access_token;
        }

        // Single call to get both connection and active status
        const response = await fetch(`${API_BASE_URL}/platform-status`, {
          credentials: 'include',
          headers: headers
        });
        logger.debug('/platform-status response:', response.ok ? 'OK' : 'FAILED');
        if (response.ok) {
          activeData = await response.json();
          logger.debug('Platform status data:', activeData);
          // Use Supabase-based connection status
          spotifyConnected = activeData.spotify.connected || false;
          youtubeConnected = activeData.youtube.connected || false;
        }
      } catch (err) {
        // Fallback to localStorage if session check fails
        logger.warn('Could not fetch platform status from server, using localStorage');
        // Direct storage access to break dependency loop (safe approach)
        const spotifyTokens = TokenStorage.getSpotifyTokens();
        const youtubeTokens = TokenStorage.getYouTubeTokens();
        spotifyConnected = !!(spotifyTokens.accessToken && spotifyTokens.refreshToken);
        youtubeConnected = !!(youtubeTokens.accessToken && youtubeTokens.refreshToken);
      }

      setPlatformStates({
        spotify: {
          connected: spotifyConnected,
          active: activeData.spotify.active && spotifyConnected,
          username: activeData.spotify.username || null,
          status: (activeData.spotify.active && spotifyConnected) ? 'active' : (spotifyConnected ? 'connected' : 'not-connected')
        },
        youtube: {
          connected: youtubeConnected,
          active: activeData.youtube.active && youtubeConnected,
          username: activeData.youtube.username || null,
          status: (activeData.youtube.active && youtubeConnected) ? 'active' : (youtubeConnected ? 'connected' : 'not-connected')
        }
      });

      logger.debug('Platform states updated from localStorage:', {
        spotify: { connected: spotifyConnected, active: activeData.spotify.active },
        youtube: { connected: youtubeConnected, active: activeData.youtube.active }
      });

    } catch (error) {
      logger.error('Error checking platform states:', error);
    }
  }, []); // Removed getTokenInfo dependency - call it directly when needed

  /**
   * Switch platform and navigate to dashboard
   */
  const switchToPlatformAndNavigate = useCallback(async (targetPlatform, navigate) => {
    try {
      logger.progress(`🔄 Switching to ${targetPlatform} platform...`);

      // Get Supabase auth token to send with request
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();

      const headers = { 'Content-Type': 'application/json' };
      if (session?.access_token) {
        headers['X-Supabase-Token'] = session.access_token;
      }

      // Step 1: Call Flask to switch the active platform
      const switchResponse = await fetch(`${API_BASE_URL}/switch-platform?platform=${targetPlatform}`, {
        method: 'POST',
        credentials: 'include',
        headers: headers
      });

      if (switchResponse.ok) {
        logger.success(`Successfully switched to ${targetPlatform}`);

        // ALWAYS force full page reload to dashboard - treats every entry like first load
        // This ensures clean initialization every time (no stale state, no race conditions)
        logger.debug('🔄 Forcing full page reload to dashboard (treating like first load)');
        window.location.href = '/dashboard';
      } else {
        const errorData = await switchResponse.json().catch(() => ({ error: 'Unknown error' }));
        logger.error(`Failed to switch to ${targetPlatform}: ${errorData.error}`);
        // Don't navigate if switch failed - stay on platforms page
        logger.warn('Platform switch failed - staying on /platforms page');
      }

    } catch (error) {
      logger.error(`Error switching to ${targetPlatform}:`, error);
      // Don't navigate on error - stay on platforms page
      logger.warn('Platform switch error - staying on /platforms page');
    }
  }, []);

  /**
   * Individual platform logout
   */
  const handlePlatformLogout = useCallback(async (platform) => {
    try {
      logger.debug(`🔄 Logging out of ${platform}...`);

      // Get Supabase auth token to send with request (required for backend to clear Supabase tokens)
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();

      const headers = {};
      if (session?.access_token) {
        headers['X-Supabase-Token'] = session.access_token;
      }

      const logoutResponse = await fetch(`${API_BASE_URL}/logout?platform=${platform}`, {
        method: 'GET',
        credentials: 'include',
        headers: headers
      });

      if (logoutResponse.ok) {
        logger.success(`${platform} logout successful`);

        // Clear tokens from localStorage
        if (platform === 'spotify') {
          TokenStorage.clearSpotifyTokens();
          logger.debug('Cleared Spotify tokens from localStorage');
        } else if (platform === 'youtube') {
          TokenStorage.clearYouTubeTokens();
          logger.debug('Cleared YouTube tokens from localStorage');
        }

        // Refresh platform states to show updated connection status
        setTimeout(() => {
          checkPlatformStates();
        }, 500);
      } else {
        logger.error(`${platform} logout failed`);
      }
    } catch (error) {
      logger.error(`Error logging out of ${platform}:`, error);
    }
  }, [checkPlatformStates]);


  /**
   * Dashboard platform logout handler
   */
  const handleDashboardPlatformLogout = useCallback(async (platform, currentPlatform, navigate) => {
    try {
      logger.debug(`🔄 Logging out of ${platform} from dashboard bubble...`);

      // Get Supabase auth token to send with request (required for backend to clear Supabase tokens)
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();

      const headers = {};
      if (session?.access_token) {
        headers['X-Supabase-Token'] = session.access_token;
      }

      const logoutResponse = await fetch(`${API_BASE_URL}/logout?platform=${platform}`, {
        method: 'GET',
        credentials: 'include',
        headers: headers
      });
      
      if (logoutResponse.ok) {
        logger.success(`${platform} logout successful from dashboard`);
        // Refresh platform states
        await checkPlatformStates();
        // If we logged out of the current active platform, redirect to platforms
        if (currentPlatform === platform) {
          navigate('/platforms');
        }
      } else {
        logger.error(`${platform} logout failed from dashboard`);
      }
    } catch (error) {
      logger.error(`Error logging out of ${platform} from dashboard:`, error);
    }
  }, [checkPlatformStates]);

  /**
   * Enhanced platform action handlers with seamless platform switching
   */
  const handleSpotifyAction = useCallback(async (navigate) => {
    const spotify = platformStates.spotify;

    if (!spotify.connected) {
      // Connect to Spotify (forced login)
      // Get user_id from Supabase session to pass to backend
      const { supabase } = await import('../lib/supabase');
      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        window.location.href = `${API_BASE_URL}/login?force=true&user_id=${user.id}`;
      } else {
        logger.error('No Supabase user found - cannot initiate Spotify OAuth');
        // Redirect to auth page
        navigate('/');
      }
    } else {
      // Switch to Spotify platform and then go to dashboard
      await switchToPlatformAndNavigate('spotify', navigate);
    }
  }, [platformStates.spotify, switchToPlatformAndNavigate]);

  // PHASE 1: YouTube disabled for Spotify-only launch
  // PHASE 2: Use this as template for Apple Music integration
  // const handleYouTubeAction = useCallback(async (navigate) => {
  //   const youtube = platformStates.youtube;

  //   if (!youtube.connected) {
  //     // Connect to YouTube (forced login)
  //     // Get user_id from Supabase session to pass to backend
  //     const { supabase } = await import('../lib/supabase');
  //     const { data: { user } } = await supabase.auth.getUser();

  //     if (user) {
  //       window.location.href = `${API_BASE_URL}/youtube-login?force=true&user_id=${user.id}`;
  //     } else {
  //       logger.error('No Supabase user found - cannot initiate YouTube OAuth');
  //       // Redirect to auth page
  //       navigate('/');
  //     }
  //   } else {
  //     // Switch to YouTube platform and then go to dashboard
  //     await switchToPlatformAndNavigate('youtube', navigate);
  //   }
  // }, [platformStates.youtube, switchToPlatformAndNavigate]);

  /**
   * Force fresh platform login
   */
  const handleForceLogin = useCallback(async (platform) => {
    logger.debug(`🔄 Forcing fresh ${platform} login...`);

    // Get user_id from Supabase session to pass to backend
    const { supabase } = await import('../lib/supabase');
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      logger.error('No Supabase user found - cannot initiate OAuth');
      return;
    }

    if (platform === 'spotify') {
      window.location.href = `${API_BASE_URL}/login?force=true&user_id=${user.id}`;
    } else if (platform === 'youtube') {
      window.location.href = `${API_BASE_URL}/youtube-login?force=true&user_id=${user.id}`;
    }
  }, []);

  /**
   * Get status text with username display
   */
  const getStatusText = useCallback((platform) => {
    const platformState = platformStates[platform];
    const username = platformState.username;
    
    if (platformState.active && username) {
      return `✅ ${username} (Active)`;
    } else if (platformState.connected && username) {
      return `🔗 ${username} (Connected)`;
    } else if (platformState.connected) {
      return '🔗 Connected';
    } else {
      return '❌ Not Connected';
    }
  }, [platformStates]);

  /**
   * Get action button text
   */
  const getActionButtonText = useCallback((platform) => {
    const platformState = platformStates[platform];
    if (!platformState.connected) return 'Connect';
    return 'Go to Dashboard';
  }, [platformStates]);

  // Check for OAuth success on component mount
  useEffect(() => {
    logger.debug('usePlatformManagement: OAuth success effect triggered');
    logger.debug('location.pathname:', location.pathname);
    logger.debug('location.search:', location.search);

    const urlParams = new URLSearchParams(location.search);
    const connected = urlParams.get('connected');

    logger.debug('connected param:', connected);

    if (connected && (connected === 'spotify' || connected === 'youtube')) {
      logger.success(`OAuth success detected for ${connected}`);
      logger.success('OAuth success confirmed! Starting refresh sequence...');
      setConnectedPlatform(connected);
      setShowConnectionSuccess(true);

      // Clear the URL parameter after 3 seconds
      setTimeout(() => {
        logger.debug('Cleaning URL parameter...');
        setShowConnectionSuccess(false);
        // Clean URL without triggering a reload
        window.history.replaceState({}, '', '/platforms');
      }, 3000);

      // Refresh platform states to show new connection
      setTimeout(() => {
        logger.progress('About to call checkPlatformStates() after OAuth success...');
        checkPlatformStates();
      }, 1000);
    } else {
      logger.info('No OAuth success parameter detected in URL');
    }
  }, [location, checkPlatformStates]);

  // Platform state initialization effect
  useEffect(() => {
    logger.debug('usePlatformManagement: Initialization effect - calling checkPlatformStates()');
    checkPlatformStates();
  }, [checkPlatformStates]);

  return {
    // State
    platformStates,
    showConnectionSuccess,
    connectedPlatform,

    // Functions
    checkPlatformStates,
    switchToPlatformAndNavigate,
    handlePlatformLogout,
    handleDashboardPlatformLogout,
    handleSpotifyAction,
    // PHASE 1: YouTube disabled for Spotify-only launch (Phase 2: Apple Music)
    // handleYouTubeAction,
    handleForceLogin,
    getStatusText,
    getActionButtonText
  };
};